/**
 * IndustryPage — E13. Dedicated page per commercial industry at
 * /commercial-services/:type. Inherits the CommercialServicesPage
 * structural template (E0 consistency rule).
 */
import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import PublicPageNav from '../components/PublicPageNav';
import CommercialQuoteEngine from '../components/CommercialQuoteEngine';
import { INDUSTRIES, SPECIALTY_SERVICES } from '../lib/industryData';
import { getCommercialIndustryImage } from '../lib/commercialIndustryImages';
import '../styles/industry-pages.css';
import '../styles/public-page-titles.css';

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = '#C9A962';

export default function IndustryPage() {
  const { type } = useParams();
  const navigate = useNavigate();
  const industry = INDUSTRIES[type];
  const industryImage = getCommercialIndustryImage(type);
  const [line, setLine] = useState(null);
  const [engineOpen, setEngineOpen] = useState(false);

  useEffect(() => {
    document.title = industry ? `${industry.necs} — NECS Integrated Facility Care` : 'NECS Commercial Services';
    fetch(`${API}/api/commercial/service-lines?visibility=commercial`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setLine((d?.service_lines || []).find((l) => l.property_type === type) || null))
      .catch(() => {});
  }, [type, industry]);

  if (!industry) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#141414' }}>
        <Link to="/commercial-services" className="text-sm font-bold" style={{ color: GOLD }}>← Back to Commercial Services</Link>
      </div>
    );
  }

  return (
    <div
      className="necs-industry-page"
      style={{ '--industry-background-image': industryImage ? `url("${industryImage.src}")` : 'none' }}
      data-testid={`industry-page-${type}`}
    >
      <PublicPageNav testIdPrefix={`industry-${type}`} />
      <main className="necs-industry-reading max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16" data-testid="industry-reading-layout">
        <Link to="/?view=commercials" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest mb-6" style={{ color: GOLD }} data-testid="industry-back">
          <ChevronLeft className="h-4 w-4" /> Back to Commercial Services
        </Link>

        {/* Industry hero */}
        <div className="necs-industry-intro text-center mb-12" data-testid="industry-intro">
          <span className="text-4xl">{industry.icon}</span>
          <p className="text-[10px] uppercase tracking-[0.35em] mt-3 mb-2" style={{ color: GOLD }} data-testid="industry-service-line">{industry.necs} · {industry.label}</p>
          <h1 className="necs-web-page-title text-white mb-3" data-testid="industry-hero">
            {industry.hero.includes(' FOR ') ? <>
              {industry.hero.slice(0, industry.hero.indexOf(' FOR ') + 5)}
              <span className="necs-web-title-accent" data-testid="industry-hero-accent">{industry.hero.slice(industry.hero.indexOf(' FOR ') + 5)}</span>
            </> : industry.hero}
          </h1>
          <div className="flex flex-wrap justify-center gap-4 mt-6">
            <Button className="font-black uppercase tracking-widest px-7 py-6" style={{ backgroundColor: GOLD, color: '#1a1a1a', fontFamily: 'Barlow Condensed, sans-serif' }}
              onClick={() => { setEngineOpen(true); setTimeout(() => document.getElementById('industry-quote')?.scrollIntoView({ behavior: 'smooth' }), 150); }} data-testid="industry-cta-quote">
              GET A QUOTE
            </Button>
            <Button className="font-black uppercase tracking-widest px-7 py-6" style={{ backgroundColor: 'transparent', color: GOLD, border: `2px solid ${GOLD}`, fontFamily: 'Barlow Condensed, sans-serif' }}
              onClick={() => navigate('/commercial-services#assessment')} data-testid="industry-cta-assessment">
              REQUEST A FACILITY ASSESSMENT
            </Button>
          </div>
        </div>

        {/* Industry problems */}
        <section className="mb-12" data-testid="industry-problems">
          <h2 className="necs-industry-section-title" data-testid="industry-problems-heading">
            WHAT {industry.label.toUpperCase()} DEAL WITH EVERY DAY
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="industry-problems-list">
            {industry.problems.map((p, i) => (
              <Card key={p} className="necs-industry-bordered p-4" data-testid={`industry-problem-${i}`}>
                <p className="text-sm text-zinc-200 leading-relaxed">{p}</p>
              </Card>
            ))}
          </div>
        </section>

        {/* Package tiers from live catalog */}
        {line && (line.tiers || []).length > 0 && (
          <section className="mb-12" data-testid="industry-packages">
            <h2 className="necs-industry-section-title" data-testid="industry-packages-heading">
              {industry.necs.toUpperCase()} <span style={{ color: GOLD }}>PACKAGES</span>
            </h2>
            <div className="grid md:grid-cols-3 gap-5" data-testid="industry-package-comparison">
              {line.tiers.map((t) => (
                <Card key={t.key} className="necs-industry-bordered min-w-0 p-5 flex flex-col" style={{ border: `2px solid ${t.key === 'standard' ? GOLD : '#52525b'}` }} data-testid={`industry-tier-${t.key}`}>
                  <h3 className="text-base font-black uppercase tracking-widest text-center mb-1" style={{ color: GOLD }} data-testid={`industry-tier-title-${t.key}`}>{t.name}</h3>
                  <p className="text-[10px] uppercase tracking-widest text-center text-zinc-300 mb-4" data-testid={`industry-tier-frequency-${t.key}`}>{t.frequency}</p>
                  <ul className="space-y-2 flex-1">
                    {(t.included || []).map((f, i) => (
                      <li key={i} className="flex items-start text-xs leading-relaxed text-zinc-200" data-testid={`industry-tier-${t.key}-feature-${i}`}>
                        <CheckCircle2 className="h-3.5 w-3.5 mr-2 mt-0.5 flex-shrink-0" style={{ color: GOLD }} />{f}
                      </li>
                    ))}
                  </ul>
                </Card>
              ))}
            </div>
            {line.disclaimer && (
              <p className="text-xs italic leading-relaxed text-center text-zinc-300 mt-4" data-testid="industry-disclaimer">{line.disclaimer}</p>
            )}
          </section>
        )}

        {/* Specialty services */}
        <section className="mb-12" data-testid="industry-specialty">
          <h2 className="necs-industry-section-title" data-testid="industry-specialty-heading">
            SPECIALTY <span style={{ color: GOLD }}>ADD-ONS</span>
          </h2>
          <div className="flex flex-wrap justify-center gap-2">
            {SPECIALTY_SERVICES.map((s) => (
              <span key={s.name} className="px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider text-center" style={{ color: GOLD, border: `1px solid ${GOLD}`, backgroundColor: 'rgba(15,15,15,0.72)' }} data-testid={`industry-specialty-${s.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}>{s.name}</span>
            ))}
          </div>
        </section>

        {/* Quote engine pre-set to this industry */}
        <section id="industry-quote" className="mb-12 scroll-mt-24">
          <h2 className="necs-industry-section-title" data-testid="industry-quote-heading">
            BUILD MY <span style={{ color: GOLD }}>SERVICE PROGRAM</span>
          </h2>
          {engineOpen ? (
            <CommercialQuoteEngine initialType={type} />
          ) : (
            <div className="text-center">
              <Button className="font-black uppercase tracking-widest px-10 py-6 text-base"
                style={{ backgroundColor: GOLD, color: '#1a1a1a', fontFamily: 'Barlow Condensed, sans-serif' }}
                onClick={() => setEngineOpen(true)}
                data-testid="reveal-industry-engine-btn">
                GET A QUOTE
              </Button>
            </div>
          )}
        </section>

        {/* Industry FAQs */}
        <section data-testid="industry-faqs">
          <h2 className="necs-industry-section-title" data-testid="industry-faqs-heading">
            {industry.necs.toUpperCase()} <span style={{ color: GOLD }}>FAQS</span>
          </h2>
          <div className="max-w-3xl mx-auto space-y-3">
            {industry.faqs.map((f, i) => (
              <Card key={i} className="necs-industry-bordered p-4" data-testid={`industry-faq-${i}`}>
                <p className="text-sm font-bold text-white mb-1">{f.q}</p>
                <p className="text-xs leading-relaxed text-zinc-300">{f.a}</p>
              </Card>
            ))}
          </div>
        </section>

        {/* Final CTA (E29) */}
        <section className="necs-industry-bordered mt-14 text-center rounded-xl p-5 sm:p-8" style={{ border: `1px solid ${GOLD}` }} data-testid="industry-final-cta">
          <h2 className="text-2xl font-black text-white uppercase mb-3" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="industry-final-heading">
            READY TO ELEVATE YOUR <span style={{ color: GOLD }}>FACILITY?</span>
          </h2>
          <p className="text-sm text-zinc-300 leading-relaxed max-w-2xl mx-auto mb-6" data-testid="industry-final-description">
            Tell NECS about your property, business or facility. We'll help develop a service program based on your operational requirements, facility size and service priorities.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button style={{ backgroundColor: GOLD, color: '#1a1a1a' }} className="font-bold uppercase tracking-widest" onClick={() => { setEngineOpen(true); setTimeout(() => document.getElementById('industry-quote')?.scrollIntoView({ behavior: 'smooth' }), 150); }} data-testid="industry-final-quote">GET A QUOTE</Button>
            <Button style={{ backgroundColor: 'transparent', color: GOLD, border: `2px solid ${GOLD}` }} className="font-bold uppercase tracking-widest" onClick={() => navigate('/commercial-services#assessment')} data-testid="industry-final-assessment">REQUEST A FACILITY ASSESSMENT</Button>
            <Button style={{ backgroundColor: 'transparent', color: GOLD, border: `2px solid ${GOLD}` }} className="font-bold uppercase tracking-widest" onClick={() => window.dispatchEvent(new CustomEvent('necs:open-support-chat', { detail: { intent: 'commercial_quote' } }))} data-testid="industry-final-sales">TALK TO SALES</Button>
          </div>
        </section>
      </main>
    </div>
  );
}
